/**
 * API Service for NearByService
 * Uses optimized httpClient with caching and request deduplication
 */

import { httpClient } from './httpClient';
import { ENDPOINTS } from '../config/api.config';

// ═══ SERVICES ═══════════════════════

// GET - Fetch all services with filters
export const fetchServices = async (filters = {}) => {
    const params = {
        category_id: filters.category,
        city_id: filters.city,
        area_id: filters.area,
        search: filters.search,
        user_id: filters.user_id,
        lat: filters.lat,
        lng: filters.lng,
        radius: filters.radius || 60,
        per_page: filters.per_page,
        page: filters.page,
    };

    return httpClient.get(ENDPOINTS.SERVICES, params, { useCache: true });
};

// GET - Fetch single service by ID
export const fetchServiceById = async (id) => {
    return httpClient.get(ENDPOINTS.SERVICE_BY_ID(id), null, { useCache: true });
};

// POST - Create new service
export const createService = async (serviceData) => {
    return httpClient.post(ENDPOINTS.SERVICES, serviceData);
};

// ═══ PRODUCTS ═══════════════════════

// POST - Create new product
export const createProduct = async (productData) => {
    return httpClient.post(ENDPOINTS.PRODUCTS, productData);
};

// GET - Fetch all products with filters
export const fetchProducts = async (filters = {}) => {
    const params = {
        category_id: filters.category,
        area_id: filters.area,
        search: filters.search,
        user_id: filters.user_id,
        per_page: filters.per_page,
        page: filters.page,
    };

    return httpClient.get(ENDPOINTS.PRODUCTS, params, { useCache: true });
};

// ═══ VENDORS ═══════════════════════

// GET - Fetch all vendors
export const fetchVendors = async () => {
    return httpClient.get(ENDPOINTS.VENDORS, null, { useCache: true });
};

// GET - Fetch vendor by ID with services and reviews
export const fetchVendorById = async (id) => {
    return httpClient.get(ENDPOINTS.VENDOR_BY_ID(id), { include: 'services,reviews' }, { useCache: true });
};

// GET - Fetch vendor with all related data (categories, tags, etc)
export const fetchVendorWithAllData = async (userId) => {
    const data = await httpClient.get(ENDPOINTS.VENDOR_BY_ID(userId), null, { useCache: true });
    // Update localStorage with full user data
    if (data) {
        localStorage.setItem('user', JSON.stringify(data));
    }
    return data;
};

// PUT - Update vendor profile
export const updateVendorProfile = async (userId, profileData) => {
    const data = await httpClient.put(ENDPOINTS.VENDOR_BY_ID(userId), profileData);
    // Update stored user info
    if (data.vendor) {
        localStorage.setItem('user', JSON.stringify(data.vendor));
    }
    return data;
};

// PUT - Update vendor profile with categories and tags (unified)
export const updateVendorProfileUnified = async (userId, profileData) => {
    return httpClient.put(ENDPOINTS.VENDOR_BY_ID(userId), profileData);
};

// GET - Fetch featured vendors (nearby if lat/lng provided, otherwise top-rated)
export const fetchFeaturedVendors = async (params = {}) => {
    const queryParams = {
        lat: params.lat,
        lng: params.lng,
        radius: params.radius || 20,
        limit: params.limit || 3,
    };

    return httpClient.get(ENDPOINTS.FEATURED_VENDORS, queryParams, { useCache: false });
};

// GET - Search vendors
export const searchVendors = async (query, cityId = null) => {
    const params = { q: query, city_id: cityId };
    return httpClient.get(ENDPOINTS.VENDORS, params, { useCache: false });
};

// GET - Get top-rated vendors
export const getTopRatedVendors = async (limit = 10, cityId = null) => {
    const params = { limit, city_id: cityId };
    return httpClient.get(ENDPOINTS.VENDORS, params, { useCache: true });
};

// POST - Add review for a vendor
export const addReview = async (vendorId, reviewData) => {
    return httpClient.post(ENDPOINTS.VENDOR_REVIEWS(vendorId), reviewData);
};

// ═══ SEARCH ═══════════════════════

// GET - Get search suggestions
export const fetchSearchSuggestions = async (query, location = '') => {
    try {
        const params = { q: query, location };
        return await httpClient.get(ENDPOINTS.SEARCH_SUGGESTIONS, params, { useCache: false });
    } catch (error) {
        console.error('Suggestions Error:', error);
        return { suggestions: [], vendors: [], categories: [] };
    }
};

// GET - Main search API
export const searchServices = async (query, lat = null, lng = null, page = 1, perPage = 12) => {
    const params = { q: query, page, per_page: perPage, lat, lng };
    return httpClient.get(ENDPOINTS.SEARCH, params, { useCache: false });
};

// ═══ LOCATION ═══════════════════════

// GET - Fetch all cities
export const fetchCities = async () => {
    return httpClient.get(ENDPOINTS.CITIES, null, { useCache: true });
};

// GET - Detect location using coordinates (for testing Nominatim API)
export const detectLocation = async (lat, lng) => {
    const params = { lat, lng };
    return httpClient.get(ENDPOINTS.DETECT_LOCATION, params, { useCache: false });
};

// GET - Fetch all areas
export const fetchAreas = async () => {
    return httpClient.get(ENDPOINTS.AREAS, null, { useCache: true });
};

// GET - Fetch areas by city ID
export const fetchAreasByCity = async (cityId) => {
    return httpClient.get(ENDPOINTS.AREAS_BY_CITY(cityId), null, { useCache: true });
};

// ═══ CATEGORIES ═══════════════════════

// GET - Fetch all categories
export const fetchCategories = async () => {
    try {
        const data = await httpClient.get(ENDPOINTS.CATEGORIES, null, { useCache: true });
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error('API Error fetching categories:', error);
        return [];
    }
};

// ═══ TAGS ═══════════════════════

// GET - Fetch all available tags
export const fetchAllTags = async () => {
    try {
        return await httpClient.get(ENDPOINTS.TAGS, null, { useCache: true });
    } catch (error) {
        console.error('Error fetching tags:', error);
        return [];
    }
};

// POST - Create new tag if it doesn't exist
export const createTag = async (tagName) => {
    return httpClient.post(ENDPOINTS.TAGS, { name: tagName });
};

// GET - Search tags by name
export const searchTags = async (query) => {
    return httpClient.get(ENDPOINTS.TAGS, { q: query }, { useCache: false });
};

// GET - Get vendor tags
export const getVendorTags = async (vendorId) => {
    try {
        return await httpClient.get(ENDPOINTS.VENDOR_TAGS(vendorId), null, { useCache: true });
    } catch (error) {
        console.error('Error fetching vendor tags:', error);
        return [];
    }
};

// POST - Attach tags to vendor
export const attachTagsToVendor = async (vendorId, tags) => {
    return httpClient.post(ENDPOINTS.VENDOR_TAGS(vendorId), {
        tags: tags.map(tag => ({
            id: tag.id || null,
            name: tag.name
        }))
    });
};

// ═══ USER CATEGORIES ═══════════════════════

// GET - Get all categories for a user
export const getUserCategories = async (userId) => {
    try {
        return await httpClient.get(ENDPOINTS.USER_CATEGORIES(userId), null, { useCache: true });
    } catch (error) {
        console.error('Error fetching user categories:', error);
        return [];
    }
};

// GET - Get category hierarchy for a user
export const getUserCategoryHierarchy = async (userId) => {
    try {
        return await httpClient.get(ENDPOINTS.USER_CATEGORY_HIERARCHY(userId), null, { useCache: true });
    } catch (error) {
        console.error('Error fetching category hierarchy:', error);
        return [];
    }
};

// GET - Get all categories with user selection status
export const getAllCategoriesForUser = async (userId) => {
    try {
        return await httpClient.get(ENDPOINTS.USER_CATEGORIES_ALL(userId), null, { useCache: true });
    } catch (error) {
        console.error('Error fetching categories:', error);
        return [];
    }
};

// POST - Attach categories to user
export const attachCategoriesToUser = async (userId, categoryIds) => {
    return httpClient.post(ENDPOINTS.USER_CATEGORIES(userId), { category_ids: categoryIds });
};

// DELETE - Detach categories from user
export const detachCategoriesFromUser = async (userId, categoryIds) => {
    return httpClient.delete(ENDPOINTS.USER_CATEGORIES(userId), { category_ids: categoryIds });
};

// ═══ AUTH ═══════════════════════

// POST - Register user (customer or vendor)
export const registerUser = async (userData) => {
    try {
        const data = await httpClient.post(ENDPOINTS.REGISTER, userData);
        // Store user in localStorage
        if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('user_type', data.user_type);
        } else if (data.user_id) {
            // OTP registration - store user_id for verification
            localStorage.setItem('pending_user_id', data.user_id);
        }
        return data;
    } catch (error) {
        console.error('Registration failed:', error);
        throw error;
    }
};

// POST - Login user with password
export const loginUser = async (phone, password) => {
    try {
        const data = await httpClient.post(ENDPOINTS.LOGIN, { phone, password });
        // Store user in localStorage
        if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('user_type', data.user_type);
        }
        return data;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};

// POST - Request OTP for login
export const requestOtp = async (phone) => {
    try {
        const data = await httpClient.post(ENDPOINTS.REQUEST_OTP, { phone });
        localStorage.setItem('pending_phone', phone);
        return data;
    } catch (error) {
        console.error('Failed to request OTP:', error);
        throw error;
    }
};

// POST - Verify OTP
export const verifyOtp = async (phone, otp) => {
    try {
        const data = await httpClient.post(ENDPOINTS.VERIFY_OTP, { phone, otp });
        // Store user info in localStorage
        if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
            localStorage.setItem('user_type', data.user_type);
            localStorage.removeItem('pending_phone');
            localStorage.removeItem('pending_user_id');
        }
        return data;
    } catch (error) {
        console.error('Invalid OTP:', error);
        throw error;
    }
};

// POST - Logout user
export const logoutUser = async () => {
    try {
        await httpClient.post(ENDPOINTS.LOGOUT);
    } catch (error) {
        console.error('Logout failed:', error);
    } finally {
        localStorage.removeItem('user');
        localStorage.removeItem('user_type');
        localStorage.removeItem('token');
    }
};

// GET - Get current user from localStorage
export const getCurrentUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
};

// GET - Get current user type
export const getUserType = () => {
    return localStorage.getItem('user_type') || 'customer';
};

// ═══ OTHER UTILITIES ═══════════════════════

// PUT - Update service
export const updateService = async (serviceId, serviceData) => {
    return httpClient.put(ENDPOINTS.SERVICE_BY_ID(serviceId), serviceData);
};

// POST - Create listing (service or product)
export const createListing = async (listingData) => {
    const endpoint = listingData.type === 'service' ? ENDPOINTS.SERVICES : ENDPOINTS.PRODUCTS;
    return httpClient.post(endpoint, listingData);
};
